import { getTimeInSecs, convertiSecondiAMinuti } from '../utils/analyticsUtils';
import { fetchSubcollectionData } from '../api/analyticsApi';
import * as XLSX from "xlsx";
import dayjs from "dayjs";

export function getMetricsForReport(aggregateList) {
    let totalUsers = 0;
    let totalNewUsers = 0;
    let totalSession = 0;
    let totalAvgSessionTime = 0;
    let totalAvgEngTime = 0;
    let totalSessionPerUser = 0;

    // Itera attraverso l'elenco aggregato per calcolare i totali
    aggregateList.forEach(item => {
        // Usa i valori già calcolati se esistono, altrimenti usa i valori originali
        const itemUniqueUsers = parseInt(item.totalUniqueUsers || item.uniqueUsers || 0);
        const itemNewUsers = parseInt(item.newUsersCount || item.newUserCount || 0);
        const itemSessions = parseInt(item.totalSessions || item.sessions || 0);

        totalUsers += itemUniqueUsers;
        totalNewUsers += itemNewUsers;
        totalSession += itemSessions;

        // Calcola il tempo medio solo se ci sono sessioni
        if (itemSessions > 0) {
            const sessionTimeInSecs = parseFloat(getTimeInSecs(item.avgSessionTime) || 0);
            totalAvgSessionTime += sessionTimeInSecs * itemSessions;

            // Se c'è avgEngagementTime lo usiamo, altrimenti usiamo avgSessionTime
            const engTimeInSecs = parseFloat(getTimeInSecs(item.avgEngagementTime) || sessionTimeInSecs);
            totalAvgEngTime += engTimeInSecs * itemUniqueUsers;
        }

        // Calcola sessioni per utente solo se ci sono utenti
        if (itemUniqueUsers > 0) {
            totalSessionPerUser += itemSessions / itemUniqueUsers;
        }
    });

    // Calcola il tempo medio di sessione e l'engagement time medio
    const avgSessionTimeTotal = totalSession ? parseFloat(convertiSecondiAMinuti(totalAvgSessionTime / totalSession)) : 0;
    const avgEngTimeTotal = totalUsers ? parseFloat(convertiSecondiAMinuti(totalAvgEngTime / totalUsers)) : 0;
    const avgSessionPerUserTotal = totalUsers ? parseFloat((totalSession / totalUsers).toFixed(2)) : 0;

    // Ritorna solo i totali con valori garantiti numerici
    return {
        totalUsers: totalUsers || 0,
        totalNewUsers: totalNewUsers || 0,
        totalSession: totalSession || 0,
        avgSessionTimeTotal: avgSessionTimeTotal || 0,
        avgEngTimeTotal: avgEngTimeTotal || 0,
        avgSessionPerUserTotal: avgSessionPerUserTotal || 0
    };
}

export function generateExcelFileWithSections(aggregateList, catalogues, reportSections, startDate, endDate) {
    const wb = XLSX.utils.book_new();

    // Funzione per convertire i secondi in formato mm:ss
    const convertSecondsToMinutes = (seconds) => {
        if (!seconds || isNaN(seconds)) return "0:00";
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Calcola le metriche per tutti i cataloghi usando ESATTAMENTE la stessa logica di AnalyticsOverviewCard
    const catalogueData = catalogues.map(catalogue => {
        let catalogueSessions = [];

        if (catalogue.id === 'all') {
            catalogueSessions = aggregateList;
        } else {
            catalogueSessions = aggregateList.filter(session =>
                session.catalog === catalogue.id
            );
        }

        // REPLICA ESATTA della logica di AnalyticsOverviewCard
        let totalUsers = 0;
        let totalNewUsers = 0;
        let totalSession = 0;
        let totalAvgEngTime = 0;
        let totalAvgSessionTime = 0;

        catalogueSessions.forEach((item) => {
            // Usa gli stessi campi di AnalyticsOverviewCard
            totalUsers += item.totalUniqueUsers || 0;
            totalSession += item.totalSessions || 0;
            totalNewUsers += item.newUsersCount || 0;

            // Verifica che avgSessionTime e avgEngagementTime esistano prima di chiamare getTimeInSecs
            // ESATTAMENTE come in AnalyticsOverviewCard
            const avgSessionSecs = item.avg_session_time ? getTimeInSecs(item.avg_session_time) : 0;
            const avgEngagementSecs = item.avg_engagement_time ? getTimeInSecs(item.avg_engagement_time) : 0;

            totalAvgSessionTime += avgSessionSecs * (item.totalSessions || 0);
            totalAvgEngTime += avgEngagementSecs * (item.totalUniqueUsers || 0); // Moltiplica per il numero di utenti
        });

        // Calcola i tempi medi finali dopo il loop - ESATTAMENTE come in AnalyticsOverviewCard
        const avgTimeSecs = totalSession > 0 ? totalAvgSessionTime / totalSession : 0;
        const avgEngTimeSecs = totalUsers > 0 ? totalAvgEngTime / totalUsers : 0;
        const avgSessionPerUser = totalUsers > 0 ? parseFloat((totalSession / totalUsers).toFixed(2)) : 0;

        return {
            nomeBrand: catalogue.nameCatalog || catalogue.nome_brand || catalogue.id,
            totalUsers,
            totalNewUsers,
            totalSession,
            avgSessionTimeTotal: convertSecondsToMinutes(avgTimeSecs),
            avgEngTimeTotal: convertSecondsToMinutes(avgEngTimeSecs),
            avgSessionPerUserTotal: avgSessionPerUser
        };
    });

    // Genera le sezioni selezionate
    if (reportSections.overview) {
        const overviewData = catalogueData.map(item => ({
            'Catalogue': item.nomeBrand || "",
            'Total Users': parseInt(item.totalUsers) || 0,
            'New Users': parseInt(item.totalNewUsers) || 0,
            'Total Sessions': parseInt(item.totalSession) || 0,
            'Avg. Session Time (m:ss)': item.avgSessionTimeTotal || "0:00",
            'Avg. Engagement Time (m:ss)': item.avgEngTimeTotal || "0:00",
            'Avg. Sessions Per User': parseFloat(item.avgSessionPerUserTotal) || 0,
        }));

        // Aggiungi riga totals se ci sono più cataloghi
        if (catalogues.length > 1) {
            let totalUsers = 0;
            let totalNewUsers = 0;
            let totalSessions = 0;
            let totalAvgSessionTime = 0;
            let totalAvgEngTime = 0;

            // Calcola i totali da aggregateList (tutti i dati)
            aggregateList.forEach((item) => {
                totalUsers += item.totalUniqueUsers || 0;
                totalSessions += item.totalSessions || 0;
                totalNewUsers += item.newUsersCount || 0;

                const avgSessionSecs = item.avg_session_time ? getTimeInSecs(item.avg_session_time) : 0;
                const avgEngagementSecs = item.avg_engagement_time ? getTimeInSecs(item.avg_engagement_time) : 0;

                totalAvgSessionTime += avgSessionSecs * (item.totalSessions || 0);
                totalAvgEngTime += avgEngagementSecs * (item.totalUniqueUsers || 0);
            });

            const avgTimeSecs = totalSessions > 0 ? totalAvgSessionTime / totalSessions : 0;
            const avgEngTimeSecs = totalUsers > 0 ? totalAvgEngTime / totalUsers : 0;
            const avgSessionPerUser = totalUsers > 0 ? parseFloat((totalSessions / totalUsers).toFixed(2)) : 0;

            const totalsRow = {
                'Catalogue': 'TOTALS',
                'Total Users': totalUsers,
                'New Users': totalNewUsers,
                'Total Sessions': totalSessions,
                'Avg. Session Time (m:ss)': convertSecondsToMinutes(avgTimeSecs),
                'Avg. Engagement Time (m:ss)': convertSecondsToMinutes(avgEngTimeSecs),
                'Avg. Sessions Per User': avgSessionPerUser,
            };

            overviewData.push(totalsRow);
        }

        const ws = XLSX.utils.json_to_sheet(overviewData);
        ws['!cols'] = [
            { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 25 }, { wch: 25 }, { wch: 20 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, "Overview");
    }

    if (reportSections.variantsDetails) {
        // Replica ESATTAMENTE la logica di AnalyticsTable.js per processare le varianti
        let variantsMap = new Map();

        // Crea una mappa per i nomi dei cataloghi
        const catalogNameMap = new Map();
        catalogues.forEach(cat => {
            catalogNameMap.set(cat.id, cat.nameCatalog || cat.nome_brand || cat.id);
        });

        // Processa i dati ESATTAMENTE come fa AnalyticsTable.js
        aggregateList.forEach(aggregate => {
            if (!aggregate) return;

            const { list_variants = [], list_products = [] } = aggregate;

            // Elabora varianti - stessa logica di AnalyticsTable.js
            list_variants.forEach(variant => {
                if (!variant) return;

                const variantId = variant.variantRef || variant.modelRef;
                if (!variantId) return;

                const productId = variant.productRef || variant.glassesRef;
                if (!productId) return;

                // Trova il nome del prodotto dai list_products
                const productData = list_products.find(p =>
                    (p.productRef || p.glassesRef) === productId
                );

                const productName = productData?.productName ||
                    productData?.glassesName ||
                    productData?.nome_modello ||
                    productId;

                // Aggiorna o crea dato della variante - STESSA LOGICA dell'UI
                if (variantsMap.has(variantId)) {
                    const existingVariant = variantsMap.get(variantId);
                    existingVariant.duration += variant.time || 0;
                    existingVariant.visualization += variant.visualization || 0;
                    existingVariant.sessions += variant.numberOfSessions || 1;
                } else {
                    variantsMap.set(variantId, {
                        id: variantId,
                        name: productName, // Usa il nome del prodotto come nell'UI
                        duration: variant.time || 0,
                        visualization: variant.visualization || 0,
                        sessions: variant.numberOfSessions || 1,
                        productRef: productId,
                        sku: variant.sku || variant.skuModel || variant.modelSku || '',
                        frameColor: variant.frameColor || variant.colorFrame || variant.primaryFrameColor || ''
                    });
                }
            });
        });

        // Converte la mappa in array e rimuove il totale
        const allVariants = Array.from(variantsMap.values());

        // Formatta il tempo come nell'UI (getDurationFromSecond)
        const formatTime = (seconds) => {
            if (!seconds || isNaN(seconds)) return "0:00";
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = Math.floor(seconds % 60);

            if (hours > 0) {
                return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            } else {
                return `${minutes}:${secs.toString().padStart(2, '0')}`;
            }
        };

        // Crea i dati per Excel
        const variantsDetailsData = allVariants.map(variant => ({
            'Product Name': variant.name,
            'SKU': variant.sku || '',
            'Frame Color': variant.frameColor || '',
            'Visualizations': parseInt(variant.visualization || 0),
            'Time': formatTime(variant.duration || 0),
            'Sessions': parseInt(variant.sessions || 0)
        }));

        // Ordina per visualizzazioni (decrescente) come nell'UI
        variantsDetailsData.sort((a, b) => b.Visualizations - a.Visualizations);

        const ws2 = XLSX.utils.json_to_sheet(variantsDetailsData);
        ws2['!cols'] = [
            { wch: 25 }, // Product Name
            { wch: 15 }, // SKU
            { wch: 15 }, // Frame Color
            { wch: 15 }, // Visualizations
            { wch: 12 }, // Time
            { wch: 12 }  // Sessions
        ];
        XLSX.utils.book_append_sheet(wb, ws2, "Variants Details");
    }

    if (reportSections.deviceAnalytics) {
        // Replica la stessa logica di DeviceAnalytics.js
        const mobileCategories = [
            "android", "android instant", "ios", "ios app clip", "fbav (mobile)",
            "instagram (mobile)", "samsungbrowser (mobile)", "chrome (mobile)",
            "safari (mobile)", "firefox (mobile)"
        ];

        const desktopCategories = [
            "safari (desktop)", "chrome (desktop)", "firefox (desktop)", "samsungbrowser (desktop)"
        ];

        // Usa la stessa funzione DeviceFilter dell'UI
        const deviceStats = { mobile: 0, desktop: 0, allDevice: 0, misc: [] };
        const deviceTypeSet = new Set();
        let allDevices = 0;

        aggregateList.forEach((item) => {
            const deviceData = item.list_device_data;
            if (deviceData) {
                deviceData.forEach((device) => {
                    const deviceType = device.type?.toLowerCase();
                    const count = device.count ?? 0;

                    // Evita duplicati usando Set
                    if (!deviceTypeSet.has(device.type)) {
                        deviceTypeSet.add(device.type);
                        deviceStats.misc.push({ name: device.type, count });
                    } else {
                        const index = deviceStats.misc.findIndex((el) => el.name === device.type);
                        deviceStats.misc[index].count += count;
                    }

                    // Classifica tra mobile e desktop
                    if (mobileCategories.some((cat) => deviceType?.includes(cat))) {
                        deviceStats.mobile += count;
                    } else if (desktopCategories.some((cat) => deviceType?.includes(cat))) {
                        deviceStats.desktop += count;
                    }

                    allDevices += count;
                });
            }
        });

        deviceStats.allDevice = allDevices;

        // Crea il foglio Device Summary (come le summary cards)
        const deviceSummaryData = [
            {
                'Device Category': 'All Devices',
                'Count': deviceStats.allDevice,
                'Percentage': '100.0%'
            },
            {
                'Device Category': 'Mobile Devices',
                'Count': deviceStats.mobile,
                'Percentage': deviceStats.allDevice > 0 ?
                    `${((deviceStats.mobile / deviceStats.allDevice) * 100).toFixed(1)}%` : '0.0%'
            },
            {
                'Device Category': 'Desktop Devices',
                'Count': deviceStats.desktop,
                'Percentage': deviceStats.allDevice > 0 ?
                    `${((deviceStats.desktop / deviceStats.allDevice) * 100).toFixed(1)}%` : '0.0%'
            }
        ];

        const ws3 = XLSX.utils.json_to_sheet(deviceSummaryData);
        ws3['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws3, "Device Summary");

        // Crea il foglio Device Details (come il bar chart)
        const deviceDetailsData = deviceStats.misc
            .sort((a, b) => b.count - a.count)
            .map(device => {
                const total = deviceStats.allDevice;
                const percentage = total > 0 ? ((device.count / total) * 100).toFixed(1) : '0.0';
                return {
                    'Device Type': device.name,
                    'Count': device.count,
                    'Percentage': `${percentage}%`
                };
            });

        const ws4 = XLSX.utils.json_to_sheet(deviceDetailsData);
        ws4['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws4, "Device Details");
    }

    // Genera il nome del file
    const dateString = dayjs().format("YYYY-MM-DD");
    let fileName = `ARShades_Analytics_Report_${dateString}`;

    // Aggiungi le sezioni selezionate al nome del file
    const selectedSections = [];
    if (reportSections.overview) selectedSections.push("Overview");
    if (reportSections.variantsDetails) selectedSections.push("VariantsDetails");
    if (reportSections.deviceAnalytics) selectedSections.push("DeviceAnalytics");

    if (selectedSections.length > 0) {
        fileName += `-(${selectedSections.join(",")})`;
    }

    fileName += ".xlsx";

    XLSX.writeFile(wb, fileName);
    return fileName;
}

export function generateExcelFile(data, selectedOptions) {
    const wb = XLSX.utils.book_new();

    Object.keys(data).forEach((category) => {

        // Normalizza i dati prima di generare il foglio
        const formattedData = data[category].map((item, index) => {
            // Gestione speciale per i tempi medi
            let avgSessionTime = item.avgSessionTimeTotal;
            let avgEngTime = item.avgEngTimeTotal;

            // Assicurati che abbiano un valore
            if (avgSessionTime === undefined || avgSessionTime === null) {
                avgSessionTime = "0:00";
            }

            if (avgEngTime === undefined || avgEngTime === null) {
                avgEngTime = "0:00";
            }

            // Assicurati che tutti i valori siano formattati correttamente e non NaN
            const formattedItem = {
                'Brand': item.nomeBrand || "",
                'Total Users': parseInt(item.totalUsers) || 0,
                'New Users': parseInt(item.totalNewUsers) || 0,
                'Total Sessions': parseInt(item.totalSession) || 0,
                'Avg. Session Time (m:ss)': avgSessionTime || "0:00",
                'Avg. Engagement Time (m:ss)': avgEngTime || "0:00",
                'Avg. Sessions Per User': parseFloat(item.avgSessionPerUserTotal) || 0,
            };

            return formattedItem;
        });

        // Genera il foglio con intestazioni personalizzate
        const ws = XLSX.utils.json_to_sheet(formattedData, {
            header: [
                'Brand',
                'Total Users',
                'New Users',
                'Total Sessions',
                'Avg. Session Time (m:ss)',
                'Avg. Engagement Time (m:ss)',
                'Avg. Sessions Per User'
            ]
        });

        // Imposta larghezza colonne
        const colWidths = [
            { wch: 20 },  // Brand
            { wch: 15 },  // Total Users
            { wch: 15 },  // New Users
            { wch: 15 },  // Total Sessions 
            { wch: 25 },  // Avg. Session Time
            { wch: 25 },  // Avg. Engagement Time
            { wch: 20 },  // Avg. Sessions Per User
        ];

        ws['!cols'] = colWidths;

        const sheetNames = {
            "ALL SERVICES": "ALL SERVICES",
            "WEB VTO": "WEB VTO",
            "3D VIEWER": "3D-AR VIEWER",
            "APP": "VTO APPS",
        };

        const sheetName = sheetNames[category] || "Report";
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // Utilizzo di Day.js per ottenere la data attuale
    const dateString = dayjs().format("YYYY-MM-DD");
    let fileName = `ARShades_Report_${dateString}`;

    // Lista delle opzioni che rappresentano tutte le scelte
    const allOptions = ["all", "WEB VTO", "3D VIEWER", "APP"];

    // Controlla se selectedOptions contiene tutte le opzioni o 'all'
    const allOptionsSelected =
        selectedOptions.includes("all") ||
        allOptions.every(option =>
            selectedOptions.includes(option) ||
            (option === "all" && selectedOptions.some(o => o.toLowerCase() === "all")));

    if (allOptionsSelected) {
        fileName += "-(ALL)";
    } else if (selectedOptions.length) {
        const optionsString = selectedOptions
            .filter(opt => opt !== "all")
            .map(option => {
                if (option.toUpperCase() === "APP") return "VTO APPS";
                return option.toUpperCase();
            })
            .join(",");
        fileName += `-(${optionsString})`;
    }

    fileName += ".xlsx";

    XLSX.writeFile(wb, fileName);
    return fileName;
}

export const enrichDataWithSubcollections = async (aggregateData) => {
    try {
        const enrichedDataPromises = aggregateData.map(async (doc) => {
            const productsAnalytics = await fetchSubcollectionData(
                doc.id,
                "ProductsAnalytics"
            );

            // Estrai list_products e list_variants e assegnali direttamente al documento
            const list_products =
                productsAnalytics.length > 0
                    ? productsAnalytics[0].list_products || []
                    : [];
            const list_variants =
                productsAnalytics.length > 0
                    ? productsAnalytics[0].list_variants || []
                    : [];

            // Conserviamo anche i vecchi nomi per retrocompatibilità
            const glassesArray = list_products;
            const variantsArray = list_variants;

            return {
                ...doc,
                list_products,
                list_variants,
                glassesArray,
                variantsArray
            };
        });

        const enrichedData = await Promise.all(enrichedDataPromises);
        return enrichedData;
    } catch (error) {
        console.error("Error enriching data with subcollections:", error);
        return aggregateData;
    }
};